import { Component, type ErrorInfo, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-white flex flex-col">
          <nav className="bg-white border-b border-gray-200 py-4">
            <div className="max-w-7xl mx-auto px-6">
              <span className="text-2xl font-bold tracking-tight text-navy">
                STEADFAST <span className="text-gold">HSA</span>
              </span>
            </div>
          </nav>
          <div className="flex-1 flex items-center justify-center px-6 py-20">
            <div className="text-center max-w-md">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <div className="h-1 w-16 bg-gold rounded-full mx-auto mb-6" />
              <h1 className="text-2xl font-bold text-navy mb-4">Something went wrong</h1>
              <p className="text-slate-500 mb-10 leading-relaxed">
                An unexpected error occurred. Your data is safe — try refreshing the page or returning to the dashboard.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => this.setState({ hasError: false })}
                  className="bg-navy text-white px-6 py-3 rounded-lg font-bold hover:opacity-90 transition"
                >
                  Try Again
                </button>
                <a
                  href="/dashboard"
                  className="border border-gray-300 text-navy px-6 py-3 rounded-lg font-bold hover:border-navy transition"
                >
                  Go to Dashboard
                </a>
              </div>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
